import {
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
  GiftDeliverySummary,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  GiftOrderForAdmin,
  GiftOrderItemType,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';

export type OrderTypeFilter = 'all' | 'coupon' | 'delivery';

export const paymentStatusLabel: Record<GiftOrderPaymentStatus, string> = {
  [GiftOrderPaymentStatus.NotPaid]: '미결제',
  [GiftOrderPaymentStatus.Waiting]: '입금대기',
  [GiftOrderPaymentStatus.Paid]: '결제완료',
  [GiftOrderPaymentStatus.Confirmed]: '확정',
  [GiftOrderPaymentStatus.CancelRequested]: '취소요청',
  [GiftOrderPaymentStatus.Canceled]: '취소됨',
};

// 수동 환불(부트페이 자동취소 불가) 판별 키워드 — method에 하나라도 포함되면 대상.
// 가상계좌·무통장입금·계좌입금 등. (예: '가상계좌', '계좌 입금', '무통장입금')
// ⚠️ 백엔드와 100% 동일 규칙이어야 함 (gift-order-manual-refund-cancel-spec.md §1).
export const MANUAL_REFUND_METHOD_KEYWORDS = ['가상', '계좌', '입금'] as const;
// 무통장(입금) 결제 시 영수증 ID에 찍히는 마커.
export const MANUAL_REFUND_RECEIPT_ID = 'PaidByDeposit';

/**
 * 취소 시 부트페이 자동취소 대신 관리자 수동 환불이 필요한 결제인지 판정.
 * 전체/부분 취소가 동일 기준을 쓰도록 한 곳에서만 판단한다.
 * 카드결제('카드','신용카드' 등)는 키워드 미포함 → false(기존 자동취소 흐름).
 */
export const isManualRefundPayment = (paymentDetail?: {
  method?: string;
  receiptId?: string;
}): boolean => {
  if (!paymentDetail) return false;
  const method = paymentDetail.method ?? '';
  if (MANUAL_REFUND_METHOD_KEYWORDS.some((kw) => method.includes(kw))) return true;
  return paymentDetail.receiptId === MANUAL_REFUND_RECEIPT_ID;
};

export const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 결제일시 압축 표기 — `26.08.16 22:55` (2자리 연도·24시간제).
 * 목록 표의 결제일시 열 폭을 줄여 영수증 링크 등을 함께 놓기 위한 포맷.
 */
export const formatDateTimeCompact = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(d.getFullYear() % 100)}.${p2(d.getMonth() + 1)}.${p2(d.getDate())} ${p2(
    d.getHours(),
  )}:${p2(d.getMinutes())}`;
};

export const getOrderType = (
  order: GiftOrderForAdmin,
): 'coupon' | 'delivery' | 'mixed' => {
  const hasCoupon = order.orderDetails.some((d) => d.type === GiftOrderItemType.Coupon);
  const hasDelivery = order.orderDetails.some(
    (d) => d.type === GiftOrderItemType.Delivery,
  );
  if (hasCoupon && hasDelivery) return 'mixed';
  if (hasDelivery) return 'delivery';
  return 'coupon';
};

export const getCouponStats = (order: GiftOrderForAdmin) => {
  const coupons = order.coupons;
  if (!coupons || coupons.length === 0) return null;

  const disposedCount = coupons.filter(
    (c) => c.redeemStatus === GiftCouponRedeemStatus.Disposed,
  ).length;
  const canceledCount = coupons.filter(
    (c) => c.redeemStatus === GiftCouponRedeemStatus.Canceled,
  ).length;

  const activeCoupons = coupons.filter(
    (c) =>
      c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
      c.redeemStatus !== GiftCouponRedeemStatus.Disposed,
  );

  const additionalCount = activeCoupons.filter((c) => c.isAdditional).length;
  const reissuedCount = activeCoupons.filter((c) => c.isReissued).length;
  const originalCount = activeCoupons.length - additionalCount - reissuedCount;
  const totalCount = coupons.length;
  const activeCount = activeCoupons.length;

  const notSharedCount = activeCoupons.filter(
    (c) => c.shareStatus === GiftCouponShareStatus.NotShared,
  ).length;
  const unusedCount = activeCoupons.filter(
    (c) =>
      c.redeemStatus !== GiftCouponRedeemStatus.Delivered &&
      c.redeemStatus !== GiftCouponRedeemStatus.Refunded,
  ).length;
  const deliveredCount = activeCoupons.filter(
    (c) => c.redeemStatus === GiftCouponRedeemStatus.Delivered,
  ).length;
  // 나중에 받기 — 교환신청은 안했지만 (Available) 수신번호 인증까지 마쳐 저장된 쿠폰
  const pendingLaterCount = activeCoupons.filter(
    (c) =>
      c.redeemStatus === GiftCouponRedeemStatus.Available &&
      !!c.recipient?.phoneNumber,
  ).length;

  return {
    notSharedCount,
    unusedCount,
    totalCount,
    originalCount,
    additionalCount,
    reissuedCount,
    deliveredCount,
    canceledCount,
    disposedCount,
    activeCount,
    pendingLaterCount,
  };
};

export const getIssueStats = (order: GiftOrderForAdmin) => {
  const coupons = order.coupons;
  if (!coupons || coupons.length === 0) return null;

  let issuedCount = 0;
  let requestedCount = 0;
  let completedCount = 0;

  coupons.forEach((c) => {
    if (c.issued) {
      if (c.isRenewed || c.refund?.refundedAt) {
        completedCount++;
      } else if (c.issued.requestedAt) {
        requestedCount++;
      } else {
        issuedCount++;
      }
    }
  });

  const total = issuedCount + requestedCount + completedCount;
  if (total === 0) return null;

  return { issuedCount, requestedCount, completedCount, total };
};

export const getDeliveryStats = (order: GiftOrderForAdmin) => {
  const deliveries = (order.deliveries ?? []) as GiftDeliverySummary[];
  if (deliveries.length === 0) return null;

  const totalCount = deliveries.length;
  const canceledCount = deliveries.filter(
    (d) => d.deliveryStatus === GiftDeliveryStatus.Canceled,
  ).length;
  const activeCount = totalCount - canceledCount;
  const notSharedCount = deliveries.filter(
    (d) => d.shareStatus === GiftDeliveryShareStatus.NotShared,
  ).length;
  const waitingAddressCount = deliveries.filter(
    (d) => d.deliveryStatus === GiftDeliveryStatus.WaitingAddress,
  ).length;
  const preparingCount = deliveries.filter(
    (d) => d.deliveryStatus === GiftDeliveryStatus.Preparing,
  ).length;
  const shippingCount = deliveries.filter(
    (d) => d.deliveryStatus === GiftDeliveryStatus.Shipping,
  ).length;
  const deliveredCount = deliveries.filter(
    (d) => d.deliveryStatus === GiftDeliveryStatus.Delivered,
  ).length;

  return {
    totalCount,
    notSharedCount,
    waitingAddressCount,
    preparingCount,
    shippingCount,
    deliveredCount,
    canceledCount,
    activeCount,
  };
};
